import type { ClientNoteDto } from '@fos/shared/contracts/client-notes';
import type { ClientDto } from '@fos/shared/contracts/crm';
import { Link } from 'react-router';
import { trpc } from '../../lib/trpc';
import { useAccess } from '../../lib/useAccess';
import { ErrorState, ListSkeleton, Section } from '../../ui';
import { TOUCH_TEXT } from '../mobile';
import { NoteCard, NoteComposer } from './ClientNotes';

/**
 * Przegląd klienta → „Ostatnie notatki” (Fala 10): przypięte nad trzema ostatnimi, szybkie „Dodaj notatkę…”,
 * link „Wszystkie” do zakładki Historia. Bez modułu Klienci (Edycja) — tylko podgląd.
 */

const RECENT = 3;

const linkClasses = `rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`;

export function ClientRecentNotes({ client }: { client: ClientDto }) {
  const access = useAccess();
  const pinned = trpc.history.notes.list.useQuery({ clientId: client.id, pinnedOnly: true, limit: 10 });
  const recent = trpc.history.notes.list.useQuery({ clientId: client.id, limit: RECENT + 10 });
  const canAdd = access.can('clients', 'edit') && client.canEdit;

  const pinnedItems = pinned.data?.items ?? [];
  const pinnedIds = new Set(pinnedItems.map((n) => n.id));
  const latest = (recent.data?.items ?? []).filter((n) => !pinnedIds.has(n.id)).slice(0, RECENT);
  const notes: ClientNoteDto[] = [...pinnedItems, ...latest];

  if (!canAdd && !pinned.isPending && !recent.isPending && notes.length === 0 && !pinned.isError) return null;

  return (
    <Section
      tone="strong"
      title="Ostatnie notatki"
      action={
        <Link to={`/klienci/${client.id}?zakladka=historia`} className={linkClasses}>
          Wszystkie
        </Link>
      }
    >
      {canAdd && <NoteComposer clientId={client.id} className="mt-1" />}
      {pinned.isPending || recent.isPending ? (
        <ListSkeleton rows={2} />
      ) : pinned.isError || recent.isError ? (
        <ErrorState
          compact
          title="Nie udało się wczytać notatek"
          onRetry={() => {
            void pinned.refetch();
            void recent.refetch();
          }}
          retrying={pinned.isFetching || recent.isFetching}
        />
      ) : notes.length === 0 ? (
        <p className="mt-3 text-body-s text-fg-muted">Brak notatek. Zapisz ustalenie, telefon albo uwagę o kliencie.</p>
      ) : (
        <ul className="mt-2 flex flex-col" aria-label="Ostatnie notatki">
          {notes.map((n) => (
            <li key={n.id} className="border-b border-line py-3 last:border-b-0">
              <NoteCard note={n} clamp />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
